import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '@stormwater-software/lib-ui';
import { toast } from 'react-toastify';
import authorizationAgreementApi, {
  Agreement,
  AgreementSignability,
  AgreementSignerInformation,
} from '../../../utils/api/authorizationAgreement';
import AddSignatureModal from './AddSignatureModal';

interface Props {
  show: boolean;
  onClose: () => void;
  projectId: number;
  agreement: Agreement | null;
  signability: AgreementSignability;
  signerInformation: AgreementSignerInformation;
  onSigned: () => void;
  bodyHtml: string;
}

const labelStyle: React.CSSProperties = { color: '#666', fontSize: 12 };
const valueStyle: React.CSSProperties = { fontWeight: 'bold' };
const readOnlyFieldStyle: React.CSSProperties = {
  background: '#f5f5f5',
  border: '1px solid #ddd',
  borderRadius: 3,
  padding: '6px 8px',
  fontSize: 13,
  color: '#333',
};

const SignatureBlock = ({
  role,
  name,
  title,
  signedAt,
  canSign,
  currentUserName,
  currentUserTitle,
  onSign,
  onAddSignature,
  disabled,
  hasSavedSignature,
}: {
  role: string;
  name: string | null;
  title: string | null;
  signedAt: string | null;
  canSign: boolean;
  currentUserName: string;
  currentUserTitle: string;
  onSign: () => void;
  onAddSignature: () => void;
  disabled: boolean;
  hasSavedSignature: boolean;
}) => {
  if (signedAt) {
    return (
      <div style={{ flex: 1, border: '1px solid #ccc', borderRadius: 4, padding: 16 }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 13, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
          {role} Signature
        </h4>
        <div style={{ marginBottom: 4 }}>
          <span style={labelStyle}>Name: </span>
          <strong style={valueStyle}>{name}</strong>
        </div>
        <div style={{ marginBottom: 4 }}>
          <span style={labelStyle}>Title: </span>
          <strong style={valueStyle}>{title}</strong>
        </div>
        <div>
          <span style={labelStyle}>Date: </span>
          <strong style={valueStyle}>
            {new Date(signedAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </strong>
        </div>
      </div>
    );
  }

  if (!canSign) {
    return (
      <div style={{ flex: 1, border: '1px solid #ccc', borderRadius: 4, padding: 16, backgroundColor: '#f9f9f9' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 13, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
          {role} Signature
        </h4>
        <p style={{ color: '#888', fontStyle: 'italic', margin: 0, fontSize: 13 }}>
          Awaiting {role.toLowerCase()} signature
        </p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, border: '1px solid #4a90e2', borderRadius: 4, padding: 16 }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
        {role} Signature
      </h4>
      <div style={{ marginBottom: 10 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#444', marginBottom: 4 }}>
          Full Name
        </label>
        <div style={readOnlyFieldStyle}>{currentUserName || '—'}</div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#444', marginBottom: 4 }}>
          Title / Position
        </label>
        <div style={readOnlyFieldStyle}>{currentUserTitle || '—'}</div>
      </div>

      {hasSavedSignature ? (
        <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
          Your saved signature will be applied to the agreement.
        </p>
      ) : (
        <>
          <p style={{ fontSize: 12, color: '#c0392b', margin: '0 0 12px' }}>
            Your name will be printed in a standard font. To use a
            handwritten signature, add one below.
          </p>
          <button
            className='primary small'
            style={{ marginRight: 8 }}
            disabled={disabled}
            onClick={onAddSignature}
          >
            Add Signature
          </button>
        </>
      )}

      <button className='primary small' disabled={disabled} onClick={onSign}>
        Sign
      </button>
    </div>
  );
};

const PROJECT_TYPE_OPTIONS = [
  'Land Development',
  'Home Building',
  'Industrial',
  'Linear/Utility',
  'Roadway/Transportation',
  'Commercial',
  'State',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\(\d{3}\) \d{3}-\d{4}$/;

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

type BillingState = {
  billing_customer_name: string;
  billing_co: string;
  billing_address: string;
  billing_email_1: string;
  billing_email_2: string;
  billing_email_3: string;
  billing_email_4: string;
  billing_phone_number_1: string;
  billing_name_1: string;
  billing_title_1: string;
  billing_phone_number_2: string;
  billing_name_2: string;
  billing_title_2: string;
  billing_phone_number_3: string;
  billing_name_3: string;
  billing_title_3: string;
  billing_additional_info: string;
  billing_project_type: string;
};

function validateBilling(b: BillingState): Partial<Record<keyof BillingState, string>> {
  const errors: Partial<Record<keyof BillingState, string>> = {};
  if (!b.billing_customer_name.trim()) errors.billing_customer_name = 'Required';
  if (!b.billing_address.trim()) errors.billing_address = 'Required';
  if (!b.billing_email_1.trim()) {
    errors.billing_email_1 = 'Required';
  } else if (!EMAIL_RE.test(b.billing_email_1.trim())) {
    errors.billing_email_1 = 'Invalid email address';
  }
  if (b.billing_email_2.trim() && !EMAIL_RE.test(b.billing_email_2.trim()))
    errors.billing_email_2 = 'Invalid email address';
  if (b.billing_email_3.trim() && !EMAIL_RE.test(b.billing_email_3.trim()))
    errors.billing_email_3 = 'Invalid email address';
  if (b.billing_email_4.trim() && !EMAIL_RE.test(b.billing_email_4.trim()))
    errors.billing_email_4 = 'Invalid email address';
  if (!b.billing_phone_number_1.trim()) {
    errors.billing_phone_number_1 = 'Required';
  } else if (!PHONE_RE.test(b.billing_phone_number_1.trim())) {
    errors.billing_phone_number_1 = 'Must be (XXX) XXX-XXXX';
  }
  if (!b.billing_name_1.trim()) errors.billing_name_1 = 'Required';
  if (!b.billing_title_1.trim()) errors.billing_title_1 = 'Required';
  if (
    b.billing_phone_number_2.trim() &&
    !PHONE_RE.test(b.billing_phone_number_2.trim())
  )
    errors.billing_phone_number_2 = 'Must be (XXX) XXX-XXXX';
  if (
    b.billing_phone_number_3.trim() &&
    !PHONE_RE.test(b.billing_phone_number_3.trim())
  )
    errors.billing_phone_number_3 = 'Must be (XXX) XXX-XXXX';
  if (!b.billing_project_type) errors.billing_project_type = 'Required';
  return errors;
}

const inputStyle = (hasError = false): React.CSSProperties => ({
  width: '100%',
  border: `1px solid ${hasError ? '#c00' : '#ccc'}`,
  borderRadius: 3,
  padding: '5px 8px',
  fontSize: 13,
  boxSizing: 'border-box',
});

const fieldLabel = (text: string, required = false): React.ReactNode => (
  <label style={{ display: 'block', fontSize: 12, color: '#444', marginBottom: 3, fontWeight: 600 }}>
    {text}{required && <span style={{ color: '#c00', marginLeft: 2 }}>*</span>}
  </label>
);

const FieldError = ({ msg }: { msg?: string }) =>
  msg ? <div style={{ color: '#c00', fontSize: 11, marginTop: 2 }}>{msg}</div> : null;

const AuthorizationAgreementModal: React.FC<Props> = ({
  show,
  onClose,
  projectId,
  agreement,
  signability,
  signerInformation,
  onSigned,
  bodyHtml,
}) => {
  const hasSavedSignature = !!signerInformation?.isSignatureAdded;

  const [showAddSignature, setShowAddSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingErrors, setBillingErrors] = useState<Partial<Record<keyof BillingState, string>>>({});
  const [touched, setTouched] = useState(false);

  const [billing, setBilling] = useState({
    billing_customer_name: agreement?.billing_customer_name ?? '',
    billing_co: agreement?.billing_co ?? '',
    billing_address: agreement?.billing_address ?? '',
    billing_email_1: agreement?.billing_email_1 ?? '',
    billing_email_2: agreement?.billing_email_2 ?? '',
    billing_email_3: agreement?.billing_email_3 ?? '',
    billing_email_4: agreement?.billing_email_4 ?? '',
    billing_phone_number_1: agreement?.billing_phone_number_1 ?? '',
    billing_name_1: agreement?.billing_name_1 ?? '',
    billing_title_1: agreement?.billing_title_1 ?? '',
    billing_phone_number_2: agreement?.billing_phone_number_2 ?? '',
    billing_name_2: agreement?.billing_name_2 ?? '',
    billing_title_2: agreement?.billing_title_2 ?? '',
    billing_phone_number_3: agreement?.billing_phone_number_3 ?? '',
    billing_name_3: agreement?.billing_name_3 ?? '',
    billing_title_3: agreement?.billing_title_3 ?? '',
    billing_additional_info: agreement?.billing_additional_info ?? '',
    billing_project_type: agreement?.billing_project_type ?? '',
  });

  useEffect(() => {
    if (agreement) {
      setBilling({
        billing_customer_name: agreement.billing_customer_name ?? '',
        billing_co: agreement.billing_co ?? '',
        billing_address: agreement.billing_address ?? '',
        billing_email_1: agreement.billing_email_1 ?? '',
        billing_email_2: agreement.billing_email_2 ?? '',
        billing_email_3: agreement.billing_email_3 ?? '',
        billing_email_4: agreement.billing_email_4 ?? '',
        billing_phone_number_1: agreement.billing_phone_number_1 ?? '',
        billing_name_1: agreement.billing_name_1 ?? '',
        billing_title_1: agreement.billing_title_1 ?? '',
        billing_phone_number_2: agreement.billing_phone_number_2 ?? '',
        billing_name_2: agreement.billing_name_2 ?? '',
        billing_title_2: agreement.billing_title_2 ?? '',
        billing_phone_number_3: agreement.billing_phone_number_3 ?? '',
        billing_name_3: agreement.billing_name_3 ?? '',
        billing_title_3: agreement.billing_title_3 ?? '',
        billing_additional_info: agreement.billing_additional_info ?? '',
        billing_project_type: agreement.billing_project_type ?? '',
      });
    }
  }, [agreement?.id]);

  const set = useCallback((field: keyof BillingState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const val = e.target.value;
    setBilling((prev) => {
      const next = { ...prev, [field]: val };
      if (touched) setBillingErrors(validateBilling(next));
      return next;
    });
  }, [touched]);

  const setPhone = useCallback((field: keyof BillingState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = formatPhone(e.target.value);
    setBilling((prev) => {
      const next = { ...prev, [field]: val };
      if (touched) setBillingErrors(validateBilling(next));
      return next;
    });
  }, [touched]);

  const handleSaveBilling = async () => {
    setTouched(true);
    const errors = validateBilling(billing);
    setBillingErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the errors in the billing form before saving');
      return;
    }
    setSavingBilling(true);
    try {
      await authorizationAgreementApi.saveBilling(projectId, billing);
      onSigned();
      toast.success('Billing information saved');
    } catch (err) {
      const error = err as any;
      toast.error(error?.response?.data?.error || 'Error saving billing information');
    } finally {
      setSavingBilling(false);
    }
  };

  const isSigned = !!(agreement?.client_signed_at && agreement?.inspector_signed_at);
  const billingReadOnly = isSigned;

  const handleSign = async (block: 'client' | 'inspector') => {
    setSubmitting(true);
    try {
      await authorizationAgreementApi.signBlock(projectId, block);
      toast.success(`${block === 'client' ? 'Client' : 'Inspector'} signature saved`);
      onSigned();
    } catch (err) {
      const error = err as any;
      toast.error(error?.response?.data?.error || 'Error saving signature');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      title='Authorization and Agreement for Storm-Water Inspections'
      handleClose={onClose}
      handleSubmit={onClose}
      submitBtnText='Close'
      cancelBtn={false}
    >
      <div
        style={{
          maxHeight: '60vh',
          overflowY: 'auto',
          padding: '0 4px',
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        {/* Tenant-configurable legal body (intro + clauses), rendered from the
            same token-substituted HTML used in the generated PDF. */}
        <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />

        {/* ── Exhibit A ── */}
        <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />
        <h4 style={{ margin: '0 0 4px', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Exhibit A — Billing Information
        </h4>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#666', fontStyle: 'italic' }}>
          Where are invoices being sent to
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 10 }}>
          <div>
            {fieldLabel('Billing Name', true)}
            <input style={inputStyle(!!billingErrors.billing_customer_name)} value={billing.billing_customer_name} onChange={set('billing_customer_name')} disabled={billingReadOnly} placeholder='Customer Name' />
            <FieldError msg={billingErrors.billing_customer_name} />
          </div>
          <div>
            {fieldLabel('c/o (if applicable)')}
            <input style={inputStyle()} value={billing.billing_co} onChange={set('billing_co')} disabled={billingReadOnly} placeholder='c/o' />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Billing Address', true)}
          <input style={inputStyle(!!billingErrors.billing_address)} value={billing.billing_address} onChange={set('billing_address')} disabled={billingReadOnly} placeholder='Street, City, State, Zip Code' />
          <FieldError msg={billingErrors.billing_address} />
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Email(s) for Invoices', true)}
          <input style={{ ...inputStyle(!!billingErrors.billing_email_1), marginBottom: 2 }} value={billing.billing_email_1} onChange={set('billing_email_1')} disabled={billingReadOnly} placeholder='Accounts payable email (required)' />
          <FieldError msg={billingErrors.billing_email_1} />
          <div style={{ marginTop: 4 }}>
            <input style={{ ...inputStyle(!!billingErrors.billing_email_2), marginBottom: 2 }} value={billing.billing_email_2} onChange={set('billing_email_2')} disabled={billingReadOnly} placeholder='Second email (optional)' />
            <FieldError msg={billingErrors.billing_email_2} />
          </div>
          <div style={{ marginTop: 4 }}>
            <input style={{ ...inputStyle(!!billingErrors.billing_email_3), marginBottom: 2 }} value={billing.billing_email_3} onChange={set('billing_email_3')} disabled={billingReadOnly} placeholder='Third email (optional)' />
            <FieldError msg={billingErrors.billing_email_3} />
          </div>
          <div style={{ marginTop: 4 }}>
            <input style={inputStyle(!!billingErrors.billing_email_4)} value={billing.billing_email_4} onChange={set('billing_email_4')} disabled={billingReadOnly} placeholder='Fourth email (optional)' />
            <FieldError msg={billingErrors.billing_email_4} />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Phone Number(s)', true)}
          {(
            [
              [
                'billing_phone_number_1',
                'billing_name_1',
                'billing_title_1',
                true,
              ],
              [
                'billing_phone_number_2',
                'billing_name_2',
                'billing_title_2',
                false,
              ],
              [
                'billing_phone_number_3',
                'billing_name_3',
                'billing_title_3',
                false,
              ],
            ] as [
              keyof BillingState,
              keyof BillingState,
              keyof BillingState,
              boolean,
            ][]
          ).map(([numF, nameF, titleF, isFirst], i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              {isFirst && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 4px', marginBottom: 2 }}>
                  <span style={{ fontSize: 11, color: '#444' }}>Phone<span style={{ color: '#c00' }}>*</span></span>
                  <span style={{ fontSize: 11, color: '#444' }}>Name<span style={{ color: '#c00' }}>*</span></span>
                  <span style={{ fontSize: 11, color: '#444' }}>Title<span style={{ color: '#c00' }}>*</span></span>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                <div>
                  <input style={inputStyle(!!billingErrors[numF])} value={billing[numF]} onChange={setPhone(numF)} disabled={billingReadOnly} placeholder='(XXX) XXX-XXXX' />
                  <FieldError msg={billingErrors[numF]} />
                </div>
                <div>
                  <input style={inputStyle(!!billingErrors[nameF])} value={billing[nameF]} onChange={set(nameF)} disabled={billingReadOnly} placeholder='Name' />
                  <FieldError msg={billingErrors[nameF]} />
                </div>
                <div>
                  <input style={inputStyle(!!billingErrors[titleF])} value={billing[titleF]} onChange={set(titleF)} disabled={billingReadOnly} placeholder='Title' />
                  <FieldError msg={billingErrors[titleF]} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Additional Billing Information')}
          <textarea style={{ ...inputStyle(), minHeight: 60, resize: 'vertical' } as React.CSSProperties} value={billing.billing_additional_info} onChange={set('billing_additional_info')} disabled={billingReadOnly} />
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Project Type', true)}
          <select style={inputStyle(!!billingErrors.billing_project_type)} value={billing.billing_project_type} onChange={set('billing_project_type')} disabled={billingReadOnly}>
            <option value=''>— Select —</option>
            {PROJECT_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <FieldError msg={billingErrors.billing_project_type} />
        </div>

        {!billingReadOnly && (
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <button className='primary small' onClick={handleSaveBilling} disabled={savingBilling}>
              {savingBilling ? 'Saving…' : 'Save Billing Info'}
            </button>
          </div>
        )}

        <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />

        <p style={{ textAlign: 'center', fontStyle: 'italic', color: '#555' }}>
          [Signature Page Follows]
        </p>
        <p>
          In witness whereof, the parties have executed this Agreement effective as of the date
          last set forth below.
        </p>

        <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
          <SignatureBlock
            role='Client'
            name={agreement?.client_name ?? null}
            title={agreement?.client_title ?? null}
            signedAt={agreement?.client_signed_at ?? null}
            canSign={signability.canSignClient && !agreement?.client_signed_at}
            currentUserName={signerInformation.name!!}
            currentUserTitle={signerInformation.title!!}
            onSign={() => handleSign('client')}
            onAddSignature={() => setShowAddSignature(true)}
            disabled={submitting}
            hasSavedSignature={hasSavedSignature}
          />
          <SignatureBlock
            role='Inspector'
            name={agreement?.inspector_name ?? null}
            title={agreement?.inspector_title ?? null}
            signedAt={agreement?.inspector_signed_at ?? null}
            canSign={
              signability.canSignInspector && !agreement?.inspector_signed_at
            }
            currentUserName={signerInformation.name!!}
            currentUserTitle={signerInformation.title!!}
            onSign={() => handleSign('inspector')}
            onAddSignature={() => setShowAddSignature(true)}
            disabled={submitting}
            hasSavedSignature={hasSavedSignature}
          />
        </div>
      </div>

      {showAddSignature && (
        <AddSignatureModal
          show={showAddSignature}
          onClose={() => setShowAddSignature(false)}
          onSaved={() => {
            onSigned();
            setShowAddSignature(false);
          }}
        />
      )}
    </Modal>
  );
};

export default AuthorizationAgreementModal;
